const U32_MAX = 4_294_967_295;
const SOURCE_VARIANTS = Object.freeze({
    rod: Object.freeze({ identityField: "zoneId", signalField: "rodZone" }),
    harpoon: Object.freeze({ identityField: "zoneId", signalField: "harpoonZone" }),
    hotspot: Object.freeze({ identityField: "hotspotId", signalField: "hotspot" }),
});
const ZONE_BACKED_KINDS = new Set(["rod", "harpoon"]);

function isRecord(value) {
    return Boolean(value)
        && typeof value === "object"
        && !Array.isArray(value)
        && Object.prototype.toString.call(value) === "[object Object]";
}

function defaultIssue(path, code, message) {
    throw new CalculatorFishingSourceContractError(path, code, message);
}

function exactFields(value, fields, path, issue) {
    const allowed = new Set(fields);
    for (const field of Object.keys(value)) {
        if (!allowed.has(field)) {
            issue(`${path}.${field}`, "unknownField", "unknown field");
        }
    }
    for (const field of fields) {
        if (!Object.prototype.hasOwnProperty.call(value, field)) {
            issue(`${path}.${field}`, "missingField", "missing field");
        }
    }
}

function identifier(value, path, issue) {
    if (typeof value !== "string") {
        issue(path, "invalidType", "expected a string");
        return "";
    }
    if (!value || value.trim() !== value) {
        issue(
            path,
            "invalidId",
            "expected a non-empty identifier without surrounding whitespace",
        );
    }
    return value;
}

/**
 * Canonicalizes the one source-table identity used by the calculator.
 *
 * `issue` lets aggregate-validation boundaries retain their own error type;
 * callers that need a fail-fast contract should use
 * `parseCalculatorFishingSourceV1`.
 */
export function canonicalizeCalculatorFishingSourceV1(
    value,
    path = "$.fishingSource",
    issue = defaultIssue,
) {
    if (!isRecord(value)) {
        issue(path, "invalidType", "expected an object");
        return Object.freeze({ kind: "rod", zoneId: "" });
    }
    const kind = value.kind;
    if (ZONE_BACKED_KINDS.has(kind)) {
        exactFields(value, ["kind", "zoneId"], path, issue);
        return Object.freeze({
            kind,
            zoneId: identifier(value.zoneId, `${path}.zoneId`, issue),
        });
    }
    if (kind === "hotspot") {
        exactFields(value, ["hotspotId", "kind"], path, issue);
        if (!Number.isInteger(value.hotspotId) || value.hotspotId < 1 || value.hotspotId > U32_MAX) {
            issue(
                `${path}.hotspotId`,
                "invalidInteger",
                `expected an integer from 1 through ${U32_MAX}`,
            );
        }
        return Object.freeze({ kind, hotspotId: value.hotspotId });
    }
    exactFields(value, ["kind"], path, issue);
    issue(
        `${path}.kind`,
        "unsupportedValue",
        "expected rod, harpoon, or hotspot",
    );
    return Object.freeze({ kind: "rod", zoneId: "" });
}

export class CalculatorFishingSourceContractError extends TypeError {
    constructor(path, code, message) {
        super(`calculator fishing-source contract failed at ${path}: ${message}`);
        this.name = "CalculatorFishingSourceContractError";
        this.path = path;
        this.code = code;
    }
}

export function parseCalculatorFishingModeV1(value, path = "$.fishingMode") {
    if (typeof value !== "string" || !Object.prototype.hasOwnProperty.call(SOURCE_VARIANTS, value)) {
        defaultIssue(path, "unsupportedValue", "expected rod, harpoon, or hotspot");
    }
    return value;
}

export function parseCalculatorFishingModesV1(value, path = "$.fishingModes") {
    if (!Array.isArray(value)) {
        defaultIssue(path, "invalidType", "expected an array");
    }
    const modes = value.map((mode, index) => (
        parseCalculatorFishingModeV1(mode, `${path}[${index}]`)
    ));
    const duplicateIndex = modes.findIndex((mode, index) => modes.indexOf(mode) !== index);
    if (duplicateIndex >= 0) {
        defaultIssue(
            `${path}[${duplicateIndex}]`,
            "duplicateValue",
            "duplicate fishing mode",
        );
    }
    return Object.freeze(modes);
}

export function parseCalculatorFishingSourceV1(value, path = "$.fishingSource") {
    return canonicalizeCalculatorFishingSourceV1(value, path);
}

export function calculatorFishingSourcesEqual(left, right) {
    if (!isRecord(left) || !isRecord(right)) {
        return false;
    }
    const canonicalLeft = parseCalculatorFishingSourceV1(
        left,
        "$.leftFishingSource",
    );
    const canonicalRight = parseCalculatorFishingSourceV1(
        right,
        "$.rightFishingSource",
    );
    if (canonicalLeft.kind !== canonicalRight.kind) {
        return false;
    }
    if (ZONE_BACKED_KINDS.has(canonicalLeft.kind)) {
        return canonicalLeft.zoneId === canonicalRight.zoneId;
    }
    return canonicalLeft.hotspotId === canonicalRight.hotspotId;
}

export function calculatorFishingSourceSignalPatchV1(value, path = "$.fishingSource") {
    const source = parseCalculatorFishingSourceV1(value, path);
    const variant = SOURCE_VARIANTS[source.kind];
    return Object.freeze({
        fishingMode: source.kind,
        [variant.signalField]: source.kind === "hotspot"
            ? String(source.hotspotId)
            : source[variant.identityField],
    });
}

/**
 * Resolves the active tagged source from the calculator's remembered picker
 * signals. An empty active picker is an explicit not-yet-selected UI state;
 * malformed non-empty identities remain contract failures.
 */
export function calculatorFishingSourceFromSignalsV1(
    value,
    path = "$.calculatorSignals",
) {
    if (!isRecord(value)) {
        defaultIssue(path, "invalidType", "expected an object");
    }
    const kind = value.fishingMode;
    const variant = SOURCE_VARIANTS[kind];
    if (!variant) {
        defaultIssue(
            `${path}.fishingMode`,
            "unsupportedValue",
            "expected rod, harpoon, or hotspot",
        );
    }
    const identity = value[variant.signalField];
    if (identity === undefined || identity === null || identity === "") {
        return null;
    }
    let typedIdentity = identity;
    if (kind === "hotspot") {
        if (typeof identity !== "string" || !/^[1-9][0-9]*$/.test(identity)) {
            defaultIssue(
                `${path}.${variant.signalField}`,
                "invalidId",
                "expected a canonical positive decimal hotspot ID",
            );
        }
        typedIdentity = Number(identity);
    }
    return parseCalculatorFishingSourceV1({
        kind,
        [variant.identityField]: typedIdentity,
    }, `${path}.fishingSource`);
}

export function calculatorFishingSourceLabel(value, path = "$.fishingSource") {
    const source = parseCalculatorFishingSourceV1(value, path);
    return source.kind === "hotspot"
        ? `hotspot ${source.hotspotId}`
        : `${source.kind} · ${source.zoneId}`;
}
